<template>
  <Search-Page
    v-if="pageResponse && isSearchPage"
    :is-locale-active="isLocaleActive"
  />
  <div v-else :key="componentKey">
    <LibInactiveLocaleMessage :is-locale-active="isLocaleActive" />
    <Breadcrumb v-if="showBreadcrumb" :slug="slug" />
    <LazyVisibleMappedComponent
      v-if="!isProtected || (isProtected && loggedIn)"
      hydrate-on-visible
      :item="pageResponse.page"
    />

    <div
      v-else
      style="
        padding-top: 50px;
        gap: 50px;
        display: flex;
        flex-direction: column;
      "
    >
      <div
        :class="`hero-overview theme-${
          $colorMode === 'light' ? 'light' : 'dark'
        }`"
      >
        <LoadingSkeleton style="height: 300px; width: 100%" />
      </div>
      <div
        :class="`content-section content-section--color-${
          $colorMode === 'light' ? 'light' : 'dark'
        } theme-${$colorMode === 'light' ? 'light' : 'dark'}`"
      >
        <div
          class="content - section__inner"
          style="gap: 50px; display: flex; flex-direction: column"
        >
          <LoadingSkeleton style="height: 400px; width: 60%; margin: auto" />
          <LoadingSkeleton style="height: 400px; width: 60%; margin: auto" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Breadcrumb from '~/components/global/lib/Breadcrumb.vue';
import { useContentfulPage } from '~~/composables/useContentfulPage';
import { pageToDefaultThemeMap } from '~/utils/pageToDefaultThemeMap';
import { buildUrlString } from '~/utils/buildUrlString';
import { ContentfulLivePreview } from '@contentful/live-preview';
import SearchPage from '~/components/global/lib/Search/SearchPage.vue';
import { INJECTED_COLOR_MODE } from '~/lib/constants';
import { setCurrentPageForAnalytics } from '~/composables/useCurrentPageTopics';
import { LoadingSkeleton } from '@hypercodestudio/basler-components';

const locale = useRouteParamLocale();
const slug = useSlug();
const { loggedIn } = useOidcAuth();
const {
  $colorMode,
  $globalPageSettings,
  $localeSlugMap,
  $secondaryNavigation,
  $protectedSlugs
} = useNuxtApp();

const pageResponse = await useContentfulPage({
  slug: slug.value,
  locale: locale.value
});

const isProtected = computed(() => $protectedSlugs.value.includes(slug.value));

// Component key to force re-render only when needed (e.g., after login on protected pages)
// We use a combination of slug and login state to ensure proper rendering
const componentKey = computed(() => {
  // For protected pages, include login state in the key to force re-render after login
  if (isProtected.value) {
    return `${slug.value}-${loggedIn.value ? 'logged-in' : 'logged-out'}`;
  }
  // For non-protected pages, only use the slug
  return slug.value;
});

// search action should only be added to homepage
// @see https://developers.google.com/search/docs/appearance/structured-data/sitelinks-searchbox
if (slug.value === '' && $globalPageSettings.value?.searchResultsPage?.slug) {
  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          url: buildUrlString(locale.value, '', undefined, undefined, true),
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: buildUrlString(
                locale.value,
                $globalPageSettings.value?.searchResultsPage.slug!,
                'q={search_term_string}',
                undefined,
                true
              )
            },
            'query-input': 'required name=search_term_string'
          }
        }),
        tagPriority: -1 // Wird als letztes eingefügt
      }
    ]
  });
}

const companyName = $globalPageSettings.value?.companyName ?? '';
useMetadata(pageResponse.value.page, companyName);

// Set current page for analytics topic tracking
setCurrentPageForAnalytics(
  pageResponse.value.page,
  pageResponse.value.topicSlugMapEn
);

const isSearchPage = computed(
  () => slug.value === $globalPageSettings.value?.searchResultsPage?.slug
);

const isLocaleActive = computed(
  () =>
    pageResponse.value.page.fields?.locales?.some(
      (enabledLocale) => enabledLocale.toLowerCase() === locale.value
    ) ?? true
);

const pageTheme = computed(
  () =>
    pageResponse.value.page.fields.pageTheme ??
    pageToDefaultThemeMap[pageResponse.value.page.sys.contentType.sys.id]
);

const secondaryNavigation = computed(
  () => pageResponse.value.page.fields?.secondaryNavigation ?? null
);

const showBreadcrumb = computed(
  () => pageResponse.value.page.fields.showBreadcrumb ?? true
);

watch(
  pageTheme,
  () => {
    $colorMode.value = pageTheme.value;
    inject(INJECTED_COLOR_MODE, pageTheme.value);
  },
  { immediate: true }
);

watch(
  () => pageResponse.value.localeSlugMap,
  () => {
    $localeSlugMap.value = pageResponse.value.localeSlugMap;
    const {
      public: { isPreviewEnabled }
    } = useRuntimeConfig();
    if (isPreviewEnabled && ContentfulLivePreview.initialized) {
      ContentfulLivePreview.openEntryInEditor({
        locale: 'de-DE',
        fieldId: 'internalName',
        entryId: pageResponse.value.page.sys.id
      });
    }
  },
  { immediate: true }
);

watch(
  () => secondaryNavigation,
  () => {
    $secondaryNavigation.value = secondaryNavigation.value;
  },
  { immediate: true }
);
</script>
